import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useProductLines from '@hooks/useProductLines'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ProductRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['products']

type ProductFilters = {
  isArchived?: boolean,
  productLineId?: number,
  sortBy?: 'desc',
  string?: string,
}

type UseProductsOptions = {
  filters?: ProductFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProductRequestOptions,
  sortKey?: string,
}

function useProducts(options: UseProductsOptions) {
  const { filters = {}, requestOptions, sortKey } = options

  const {
    isArchived: filterIsArchived,
    productLineId: filterProductLineId,
    sortBy: filterSortBy,
    string: filterString,
  } = filters

  const { productLines } = useProductLines({ performHttpRequests: true })
  const { name: productLineName } = productLines.find(productLine => productLine.id === filterProductLineId) || {}

  const {
    updatedEntities: { products: productsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { products } = useSelector(reduxState => reduxState.entities)

  const filteredProducts = useMemo(() => {
    const filtered = Object.values(products).filter((product) => {
      const { id, isArchived, name, productLineId } = product

      const matchIsArchived = filterIsArchived !== undefined ? isArchived === filterIsArchived : true

      const matchProductLine = matchFilterString(productLineId, productLineName)

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(name, filterString)
      const stringMatch = matchId || matchTitle

      return matchIsArchived && matchProductLine && stringMatch
    })

    return sortArrayBy(filtered, filterSortBy ?? 'asc', sortKey ?? 'name')
  }, [productsUpdatedAt, JSON.stringify(filters)])

  const filteredProductsCount = filteredProducts.length
  const hasFilteredProducts = !!filteredProductsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProductsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProducts } = useReduxAction(
    'products',
    'loadProducts',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProducts,
    filteredProductsCount,
    hasFilteredProducts,
    loading: loadingProducts,
  }
}

export default useProducts
